import React from 'react';
import { Box, DrawerV2, PopoverV2, Portal } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { pxToRem } from '@withjoy/joykit/theme';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { AutoPopulatedStayAreasContent } from './AutoPopulatedStayAreas.Content';
import { AutoPopulatedStayAreasMap } from './AutoPopulatedStayAreas.Map';
import { getValidCoordinates } from '@shared/core/places/location';
import { useInView } from '@shared/hooks/useInView';

type AutoPopulatedStayAreasTooltipProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  showCta?: boolean;
  loading?: boolean;
  onCtaClick?: () => void;
}>;

export const AutoPopulatedStayAreasTooltip: React.FC<AutoPopulatedStayAreasTooltipProps> = ({ isOpen, onClose, showCta, loading, onCtaClick }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const { ref: anchorRef, inView } = useInView<HTMLDivElement>();
  const { venueInfo } = useEventPlaces();

  const venueLocation = getValidCoordinates(venueInfo);

  const mapContent = venueLocation ? (
    <Box
      width="100%"
      height={pxToRem(261)}
      borderRadius={pxToRem(8)}
      overflow="hidden"
    >
      <JoyInteractableMapProvider>
        <AutoPopulatedStayAreasMap venueLocation={venueLocation} />
      </JoyInteractableMapProvider>
    </Box>
  ) : null;

  const isTooltipOpen = isOpen && inView;

  if (!isOpen) return null;

  return (
    <>
      {isMobileOrTablet ? (
        <DrawerV2
          disableAutoFocus
          enableReturnFocusOnClose
          useBackdrop={false}
          overrides={drawerOverrides}
          isOpen={isOpen}
          anchor="bottom"
        >
          <AutoPopulatedStayAreasContent
            onClose={onClose}
            map={mapContent}
            showCta={showCta}
            onCtaClick={onCtaClick}
          />
        </DrawerV2>
      ) : (
        <PopoverV2
          triggerType="none"
          placement="right"
          isOpen={isTooltipOpen}
          showArrow={true}
          onClose={onClose}
          popperOptions={{
            disableFlip: true,
          }}
          overrides={{
            Content: {
              props: {
                backgroundColor: 'white',
                borderRadius: pxToRem(16),
                boxShadow: boxShadowValuesV2.xxxl,
                width: pxToRem(397),
                maxWidth: pxToRem(397),
                padding: 0,
              },
            },
            Arrow: {
              props: {
                backgroundColor: 'white',
              },
            },
          }}
        >
          <PopoverV2.Trigger>
            <Box
              width={1}
              height={1}
              backgroundColor="transparent"
              position="fixed"
              right={6}
              top={pxToRem(310)}
              ref={anchorRef}
            />
          </PopoverV2.Trigger>
          <Portal appendToParentPortal>
            <PopoverV2.Content>
              <AutoPopulatedStayAreasContent
                onClose={onClose}
                map={mapContent}
                showCta={showCta}
                loading={loading}
                onCtaClick={onCtaClick}
              />
            </PopoverV2.Content>
          </Portal>
        </PopoverV2>
      )}
    </>
  );
};
